import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Trophy, 
  Package, 
  Users, 
  ArrowRight, 
  Search, 
  X, 
  Flame, 
  CheckCircle2, 
  AlertCircle, 
  Clock, 
  Calendar, 
  ChevronRight,
  ExternalLink, 
  ShieldCheck, 
  Award, 
  Link as LinkIcon,
  Eye,
  EyeOff,
  RefreshCw,
  AlertTriangle,
  Check,
  Shield,
  Edit3,
  UserCheck,
  Globe,
  Pencil,
  Mail
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { 
  competitionService, 
  CompetitionLeaderboardData, 
  CompetitionLeaderboardItem, 
  CompetitionParticipant 
} from '../services/competitionService';
import './CompetitionPage.css';

export interface CompetitionPlayer {
  id: string;
  name: string;
  store?: string;
  delivered: number;
  position?: number;
  isHidden?: boolean;
  isCurrentUser?: boolean;
  email?: string;
  userId?: string;
}

interface CompetitionPageProps {
  onBack?: () => void;
  onNavigate?: (section: string) => void;
}

export const CompetitionPage: React.FC<CompetitionPageProps> = ({ onBack, onNavigate }) => {
  const { user, isAdmin } = useAuth();

  // Estados de dados e carregamento
  const [loading, setLoading] = useState(true);
  const [leaderboardData, setLeaderboardData] = useState<CompetitionLeaderboardData | null>(null);
  const [allParticipants, setAllParticipants] = useState<CompetitionParticipant[]>([]);
  const [userParticipation, setUserParticipation] = useState<CompetitionParticipant | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllRows, setShowAllRows] = useState(false);

  // Moderação Administrativa
  const [showHiddenToAdmin, setShowHiddenToAdmin] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  // Modais de Inscrição
  const [modalStep, setModalStep] = useState<'none' | 'rules' | 'connection'>('none');
  const [hasAgreedToRules, setHasAgreedToRules] = useState(false);

  // Formulário de Inscrição (Etapa 2)
  const [formEmail, setFormEmail] = useState('');
  const [formPublicName, setFormPublicName] = useState('');
  const [validatingEmail, setValidatingEmail] = useState(false);
  const [emailValidationInfo, setEmailValidationInfo] = useState<{
    valid: boolean;
    deliveredCount: number;
    error?: string;
  } | null>(null);
  const [submittingRegistration, setSubmittingRegistration] = useState(false);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState<string | null>(null);

  // Moderação Admin: Edição de Nome do Vendedor
  const [editingPlayer, setEditingPlayer] = useState<{ id: string; name: string; email?: string } | null>(null);
  const [editingNameInput, setEditingNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [editNameError, setEditNameError] = useState<string | null>(null);

  // Contagem regressiva dinâmica (Horário de Bogotá - UTC-5)
  const [countdown, setCountdown] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    status: 'before' | 'running' | 'ended';
  }>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    status: 'running'
  });

  const fmt = useMemo(() => new Intl.NumberFormat('pt-BR'), []);

  // Timer da competição
  useEffect(() => {
    const updateCountdown = () => {
      const now = Date.now();
      const start = Date.parse('2026-11-01T00:00:00-05:00');
      const end = Date.parse('2026-12-16T00:00:00-05:00');

      if (now < start) {
        const diff = Math.max(0, Math.floor((start - now) / 1000));
        setCountdown({
          days: Math.floor(diff / 86400),
          hours: Math.floor(diff / 3600) % 24,
          minutes: Math.floor(diff / 60) % 60,
          seconds: diff % 60,
          status: 'before'
        });
      } else if (now < end) {
        const diff = Math.max(0, Math.floor((end - now) / 1000));
        setCountdown({
          days: Math.floor(diff / 86400),
          hours: Math.floor(diff / 3600) % 24,
          minutes: Math.floor(diff / 60) % 60,
          seconds: diff % 60,
          status: 'running'
        });
      } else {
        setCountdown({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          status: 'ended'
        });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  // Carregar dados da competição
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [lb, participants] = await Promise.all([
        competitionService.getLeaderboard(),
        isAdmin ? competitionService.getAllParticipants() : Promise.resolve([])
      ]);

      setLeaderboardData(lb);
      if (isAdmin) {
        setAllParticipants(participants);
      }

      const savedEmail = (typeof localStorage !== 'undefined' ? localStorage.getItem('masterclub_competition_email') : null) || user?.email;
      const mySub = await competitionService.getUserParticipation(user?.id, savedEmail);
      setUserParticipation(mySub);
      if (mySub) {
        setFormEmail(mySub.mastershopEmail);
        setFormPublicName(mySub.publicName);
      }
    } catch (err) {
      console.error('Erro ao carregar dados do Black Challenge:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, user?.id, user?.email]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Mapa de e-mails para administradores identificarem os competidores
  const emailMap = useMemo(() => {
    const map = new Map<string, string>();
    allParticipants.forEach((p) => {
      if (p.id && p.mastershopEmail) map.set(p.id, p.mastershopEmail);
      if (p.userId && p.mastershopEmail) map.set(p.userId, p.mastershopEmail);
    });
    return map;
  }, [allParticipants]);

  // Lista consolidada de competidores para exibição
  const rankedPlayers = useMemo<CompetitionPlayer[]>(() => {
    const isPlayerCurrentUser = (playerId: string, playerUserId?: string) => {
      if (userParticipation && userParticipation.id === playerId) return true;
      if (user?.id && playerUserId && user.id === playerUserId) return true;
      return false;
    };

    // 1. Se admin marcou para ver ocultos, usa a lista completa de participantes
    if (isAdmin && showHiddenToAdmin && allParticipants.length > 0) {
      const sorted = [...allParticipants].sort((a, b) => b.deliveredOrders - a.deliveredOrders);
      return sorted.map((p, idx) => ({
        id: p.id,
        name: p.publicName,
        delivered: p.deliveredOrders,
        position: idx + 1,
        isHidden: p.isHidden,
        isCurrentUser: isPlayerCurrentUser(p.id, p.userId),
        email: p.mastershopEmail,
        userId: p.userId,
      }));
    }

    // 2. Se temos ranking em cache no Supabase
    if (leaderboardData?.ranking && leaderboardData.ranking.length > 0) {
      return leaderboardData.ranking.map((item) => ({
        id: item.id,
        name: item.publicName,
        delivered: item.deliveredOrders,
        position: item.rank,
        isHidden: false,
        isCurrentUser: isPlayerCurrentUser(item.id, item.userId),
        email: emailMap.get(item.id) || (item.userId ? emailMap.get(item.userId) : undefined),
        userId: item.userId,
      }));
    }

    // 3. Se ainda não há participantes inscritos
    return [];
  }, [isAdmin, showHiddenToAdmin, allParticipants, leaderboardData, user?.id, userParticipation, emailMap]);

  // Filtragem dos jogadores por busca ou Top 5
  const displayedSellers = useMemo(() => {
    const q = searchQuery.trim().toLocaleLowerCase('pt-BR');
    if (q) {
      return rankedPlayers.filter(p => 
        p.name.toLocaleLowerCase('pt-BR').includes(q) ||
        (isAdmin && p.email && p.email.toLocaleLowerCase('pt-BR').includes(q))
      );
    }
    return showAllRows ? rankedPlayers : rankedPlayers.slice(0, 5);
  }, [rankedPlayers, searchQuery, showAllRows, isAdmin]);

  const totalDelivered = useMemo(() => {
    if (leaderboardData?.totalDeliveredGuides != null) {
      return leaderboardData.totalDeliveredGuides;
    }
    return rankedPlayers.reduce((acc, curr) => acc + curr.delivered, 0);
  }, [leaderboardData, rankedPlayers]);

  const totalCompetitors = useMemo(() => {
    if (leaderboardData?.totalEnrolledCompetitors != null) {
      return leaderboardData.totalEnrolledCompetitors;
    }
    return rankedPlayers.length;
  }, [leaderboardData, rankedPlayers]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .filter(Boolean)
      .map(part => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'MC';
  };

  const getNextRankDiff = (currentPosition: number, currentDelivered: number) => {
    if (currentPosition === 1) return 'Liderando a disputa';
    const playerAbove = rankedPlayers.find(p => p.position === currentPosition - 1);
    if (!playerAbove) return 'No topo do ranking';
    const diff = playerAbove.delivered - currentDelivered + 1;
    return `${fmt.format(diff)} ${diff === 1 ? 'entrega' : 'entregas'} para ultrapassar a posição acima`;
  };

  // Validação Mágica do e-mail MasterShop em tempo real
  const handleValidateEmail = async (emailToValidate?: string) => {
    const email = (emailToValidate || formEmail).trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setEmailValidationInfo({
        valid: false,
        deliveredCount: 0,
        error: 'Informe um e-mail válido para consultar.'
      });
      return;
    }

    setValidatingEmail(true);
    setEmailValidationInfo(null);
    try {
      const res = await competitionService.validateMastershopEmail(email);
      setEmailValidationInfo({
        valid: res.valid,
        deliveredCount: res.deliveredCount,
        error: res.error
      });
    } catch (err: any) {
      setEmailValidationInfo({
        valid: false,
        deliveredCount: 0,
        error: err.message || 'Falha ao consultar a API MasterShop.'
      });
    } finally {
      setValidatingEmail(false);
    }
  };

  // Submissão da Inscrição (Etapa 2)
  const handleSubmitRegistration = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formEmail.trim() || !formEmail.includes('@')) {
      setRegistrationError('Por favor, informe seu e-mail de acesso à MasterShop.');
      return;
    }

    if (!formPublicName.trim() || formPublicName.trim().length < 2) {
      setRegistrationError('Defina um nome público de pelo menos 2 caracteres.');
      return;
    }

    setSubmittingRegistration(true);
    setRegistrationError(null);
    setRegistrationSuccess(null);

    try {
      const res = await competitionService.registerParticipant({
        userId: user?.id || null,
        mastershopEmail: formEmail.trim(),
        publicName: formPublicName.trim()
      });

      if (!res.success) {
        setRegistrationError(res.error || 'Não foi possível concluir sua inscrição.');
        return;
      }

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('masterclub_competition_email', formEmail.trim().toLowerCase());
      }

      setRegistrationSuccess('Inscrição confirmada com sucesso! Seus dados foram sincronizados.');
      await loadData();
      setTimeout(() => {
        setModalStep('none');
        setRegistrationSuccess(null);
      }, 1500);
    } catch (err: any) {
      setRegistrationError(err.message || 'Erro inesperado ao salvar inscrição.');
    } finally {
      setSubmittingRegistration(false);
    }
  };

  // Moderação Admin: Ocultar / Reexibir Participante
  const handleToggleVisibility = async (participantId: string, currentIsHidden: boolean) => {
    if (!isAdmin) return;
    try {
      setActionLoadingId(participantId);
      const res = await competitionService.toggleParticipantVisibility(participantId, !currentIsHidden);
      if (res.success) {
        await loadData();
      } else {
        alert(res.error || 'Erro ao alterar visibilidade.');
      }
    } catch (err: any) {
      console.error('Erro na moderação:', err);
      alert(err.message || 'Falha ao executar ação.');
    } finally {
      setActionLoadingId(null);
    }
  };

  // Moderação Admin: Abrir Modal de Edição de Nome
  const handleOpenEditModal = (player: CompetitionPlayer) => {
    setEditingPlayer({
      id: player.id,
      name: player.name,
      email: player.email || emailMap.get(player.id),
    });
    setEditingNameInput(player.name);
    setEditNameError(null);
  };

  // Moderação Admin: Salvar Nome Real do Vendedor
  const handleSaveName = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!editingPlayer) return;
    const trimmed = editingNameInput.trim();
    if (!trimmed || trimmed.length < 2) {
      setEditNameError('O nome deve conter pelo menos 2 caracteres.');
      return;
    }

    setSavingName(true);
    setEditNameError(null);
    try {
      const res = await competitionService.updateParticipantName(editingPlayer.id, trimmed);
      if (res.success) {
        setEditingPlayer(null);
        await loadData();
      } else {
        setEditNameError(res.error || 'Erro ao atualizar nome.');
      }
    } catch (err: any) {
      setEditNameError(err.message || 'Falha ao atualizar no servidor.');
    } finally {
      setSavingName(false);
    }
  };

  // Moderação Admin: Forçar Sincronização Imediata com API MasterShop
  const handleForceSync = async () => {
    if (!isAdmin || syncing) return;
    try {
      setSyncing(true);
      setSyncMessage(null);
      
      const res = await fetch('/api/competition/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (res.ok) {
        const json = await res.json();
        setSyncMessage(`Sincronizado! ${json.totalGuides || 0} guias apuradas.`);
        await loadData();
      } else {
        // Fallback para recalcular via client
        await competitionService.recalculateLeaderboard(allParticipants);
        setSyncMessage('Ranking recalculado via cache do banco.');
        await loadData();
      }
    } catch (err: any) {
      console.warn('Endpoint de sync background indisponível no ambiente local, atualizando cache:', err);
      await loadData();
      setSyncMessage('Dados atualizados com sucesso.');
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMessage(null), 4000);
    }
  };

  return (
    <div className="competition-container w-full min-h-screen bg-[#121316] text-[#f5f6f8] pt-20 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="comp-content mx-auto">

        {/* Hero / Pagehead */}
        <section className="comp-pagehead">
          <div className="comp-kicker flex items-center justify-center gap-2">
            <span>MASTERCLUB BRASIL · TEMPORADA DE FIM DE ANO</span>
          </div>
          <h1 className="comp-title">
            BLACK <span>CHALLENGE</span>
          </h1>
          <p className="comp-description">
            Da Black Friday a 15 de dezembro, cada pedido entregue na MasterShop aproxima você do pódio. O primeiro lugar embarca com a gente para a Colômbia.
          </p>

          {/* Bandeiras dos Países Atendidos */}
          <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
            <div className="inline-flex items-center gap-2 bg-[#171b21] border border-[#2b3540] px-4 py-2 rounded-full text-xs text-gray-300 shadow-sm">
              <span className="text-[#00ef43] font-semibold flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" /> Países Válidos:
              </span>
              <span className="text-gray-300">Entregas contabilizadas em todos os países atendidos:</span>
              <div className="inline-flex items-center gap-1.5 font-medium text-white">
                <span className="bg-white/5 border border-white/10 px-2 py-0.5 rounded-md flex items-center gap-1">
                  🇨🇴 Colômbia
                </span>
                <span className="bg-white/5 border border-white/10 px-2 py-0.5 rounded-md flex items-center gap-1">
                  🇵🇪 Peru
                </span>
                <span className="bg-white/5 border border-white/10 px-2 py-0.5 rounded-md flex items-center gap-1">
                  🇪🇨 Equador
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Timerbar / Countdown */}
        <section className="comp-timerbar">
          <span className="comp-timer-label">
            {countdown.status === 'before' && 'A temporada começa em'}
            {countdown.status === 'running' && 'Tempo restante de vendas'}
            {countdown.status === 'ended' && 'Vendas encerradas · Apuração oficial'}
          </span>

          {countdown.status !== 'ended' && (
            <div className="comp-timebox">
              <div>
                <b>{String(countdown.days).padStart(2, '0')}</b>
                <small>dias</small>
              </div>
              <span className="colon">:</span>
              <div>
                <b>{String(countdown.hours).padStart(2, '0')}</b>
                <small>horas</small>
              </div>
              <span className="colon">:</span>
              <div>
                <b>{String(countdown.minutes).padStart(2, '0')}</b>
                <small>min</small>
              </div>
              <span className="colon">:</span>
              <div>
                <b>{String(countdown.seconds).padStart(2, '0')}</b>
                <small>seg</small>
              </div>
            </div>
          )}


        </section>

        {/* Barra de Administração (Exclusivo Administrador) */}
        {isAdmin && (
          <div className="bg-[#19222c] border border-[#2b3a4a] rounded-xl p-3.5 flex flex-wrap items-center justify-between gap-3 text-xs shadow-lg">
            <div className="flex items-center gap-2">
              <span className="bg-amber-500/20 text-amber-400 font-bold px-2 py-0.5 rounded border border-amber-500/40 text-[10px] uppercase tracking-wider flex items-center gap-1">
                <Shield className="w-3 h-3" /> Painel Admin
              </span>
              <span className="text-gray-300">
                Gerencie visibilidade de participantes e force a sincronização com a MasterShop.
              </span>
              {syncMessage && (
                <span className="text-[#00ef43] font-semibold bg-[#00ef43]/10 px-2 py-0.5 rounded border border-[#00ef43]/20">
                  {syncMessage}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 cursor-pointer text-gray-300 hover:text-white transition-colors">
                <input
                  type="checkbox"
                  checked={showHiddenToAdmin}
                  onChange={(e) => setShowHiddenToAdmin(e.target.checked)}
                  className="rounded border-gray-600 accent-[#00ef43] cursor-pointer"
                />
                <span>Exibir ocultos ({allParticipants.filter(p => p.isHidden).length})</span>
              </label>

              <button
                onClick={handleForceSync}
                disabled={syncing}
                className="flex items-center gap-1.5 bg-[#00ef43] text-[#071b0c] hover:bg-[#00df3c] px-3 py-1.5 rounded-lg font-bold text-xs transition-all shadow-md disabled:opacity-50 cursor-pointer"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Sincronizando...' : 'Forçar Sync Agora'}
              </button>
            </div>
          </div>
        )}

        {/* Arena: Tabela do Ranking + Card Resumo da Competição */}
        <div className="comp-arena">
          {/* Coluna Esquerda: Tabela de Classificação */}
          <section className="comp-ranking-card">
            <div className="comp-rankhead">
              <div>
                <h2>Ranking dos sellers</h2>
                <p>Veja quantas entregas separam você da próxima posição.</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="comp-badge">
                  {showAllRows || searchQuery ? 'TODOS OS COMPETIDORES' : 'TOP 5 SELLERS'}
                </span>
                {!searchQuery && rankedPlayers.length > 5 && (
                  <button
                    onClick={() => setShowAllRows(!showAllRows)}
                    className="text-[10px] text-[#00ef43] hover:underline font-semibold bg-[#1a2318] px-2.5 py-1 rounded border border-[#2b442a] transition-all"
                  >
                    {showAllRows ? 'Ver apenas Top 5' : `Ver todos (${rankedPlayers.length})`}
                  </button>
                )}
              </div>
            </div>

            <div className="comp-board-tools">
              <span className="flex items-center gap-1.5">
                CLASSIFICAÇÃO OFICIAL
                {leaderboardData?.updatedAt && (
                  <span className="text-[10px] text-gray-400 font-normal">
                    · Atualizado em {new Date(leaderboardData.updatedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </span>
              <div className="relative">
                <input
                  type="search"
                  placeholder="Buscar player…"
                  aria-label="Buscar player no ranking"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="comp-search-input pl-8"
                />
                <Search className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="comp-table">
                <thead>
                  <tr>
                    <th scope="col">Posição</th>
                    <th scope="col">Player</th>
                    <th scope="col" className="comp-orders-heading">Entregues</th>
                    {isAdmin && <th scope="col" className="text-right">Ação Admin</th>}
                  </tr>
                </thead>
                <tbody>
                  {displayedSellers.map((player) => {
                    const pos = player.position || 1;
                    const isPodium = pos <= 3;
                    const rowClass = isPodium ? `podium-row podium-${pos}` : '';

                    return (
                      <tr key={player.id} className={`${rowClass} ${player.isCurrentUser ? 'bg-[#00ef43]/5' : ''}`}>
                        <td>
                          <span className="position-medal">
                            {String(pos).padStart(2, '0')}
                          </span>
                        </td>
                        <td>
                          <div className="comp-person">
                            <span className="comp-avatar">
                              {getInitials(player.name)}
                            </span>
                            <div className="comp-seller-info">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <b>{player.name}</b>

                                {/* Lápis de edição rápida de nome para admin */}
                                {isAdmin && (
                                  <button
                                    onClick={() => handleOpenEditModal(player)}
                                    title="Editar nome público deste vendedor"
                                    className="text-gray-400 hover:text-[#00ef43] hover:bg-[#00ef43]/10 p-1 rounded transition-colors cursor-pointer"
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                )}

                                {/* Etiqueta Verde do E-mail MasterShop visível apenas para Admins */}
                                {isAdmin && (player.email || emailMap.get(player.id)) && (
                                  <span 
                                    className="inline-flex items-center gap-1 text-[11px] font-mono font-medium text-[#00ef43] bg-[#00ef43]/10 border border-[#00ef43]/30 px-2 py-0.5 rounded-full select-all max-w-[220px] sm:max-w-xs truncate"
                                    title={`E-mail MasterShop: ${player.email || emailMap.get(player.id)}`}
                                  >
                                    <Mail className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">{player.email || emailMap.get(player.id)}</span>
                                  </span>
                                )}

                                {player.isCurrentUser && (
                                  <span className="text-[9px] bg-[#00ef43]/20 text-[#00ef43] px-1.5 py-0.5 rounded font-bold border border-[#00ef43]/40">
                                    VOCÊ
                                  </span>
                                )}
                                {player.isHidden && (
                                  <span className="text-[9px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded font-bold border border-red-500/40">
                                    OCULTO
                                  </span>
                                )}
                              </div>
                              <small>
                                {getNextRankDiff(pos, player.delivered)}
                              </small>
                            </div>
                            {pos === 1 && !player.isHidden && (
                              <span className="on-fire" title="Líder da competição">
                                <span role="img" aria-label="On fire">🔥</span>
                                <b>ON FIRE</b>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="orders">
                          {fmt.format(player.delivered)}
                        </td>
                        {isAdmin && (
                          <td className="text-right">
                            <div className="inline-flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => handleOpenEditModal(player)}
                                disabled={actionLoadingId === player.id}
                                title="Editar nome público do vendedor"
                                className="p-1.5 rounded-lg border border-gray-700 bg-gray-800/60 text-gray-400 hover:text-[#00ef43] hover:border-[#00ef43]/40 hover:bg-[#00ef43]/10 transition-all cursor-pointer"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleToggleVisibility(player.id, !!player.isHidden)}
                                disabled={actionLoadingId === player.id}
                                title={player.isHidden ? 'Tornar visível no ranking público' : 'Ocultar do ranking público'}
                                className={`p-1.5 rounded-lg border transition-all cursor-pointer ${
                                  player.isHidden 
                                    ? 'bg-amber-500/10 border-amber-500/40 text-amber-400 hover:bg-amber-500/20' 
                                    : 'bg-gray-800/60 border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700'
                                }`}
                              >
                                {actionLoadingId === player.id ? (
                                  <RefreshCw className="w-3.5 h-3.5 animate-spin text-gray-400" />
                                ) : player.isHidden ? (
                                  <EyeOff className="w-3.5 h-3.5" />
                                ) : (
                                  <Eye className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {displayedSellers.length === 0 && !loading && (
                <div className="p-8 text-center text-gray-400 text-sm space-y-3">
                  <Trophy className="w-8 h-8 text-gray-600 mx-auto" />
                  <p>
                    {searchQuery 
                      ? `Nenhum competidor encontrado com o termo "${searchQuery}".` 
                      : 'Nenhum competidor inscrito ainda nesta edição.'}
                  </p>

                </div>
              )}
            </div>

            <div className="comp-boardfoot">
              <span>Atualização a cada uma hora</span>
            </div>
          </section>

          {/* Coluna Direita: Resumo em Números */}
          <aside className="comp-summary-card flex flex-col justify-between">
            <div className="space-y-4">
              <div className="comp-summary-title">A COMPETIÇÃO EM NÚMEROS</div>
              
              <div className="comp-summary-stat">
                <span className="comp-stat-icon">
                  <Package className="w-5 h-5 text-[#00ef43]" />
                </span>
                <small>Pedidos entregues apurados</small>
                <b>{fmt.format(totalDelivered)}</b>
                <span className="comp-stat-desc">
                  Cada entrega oficial vale 1 ponto. Consideramos entregas de todos os países: 🇨🇴 Colômbia, 🇵🇪 Peru e 🇪🇨 Equador.
                </span>
              </div>

              <div className="comp-summary-stat">
                <span className="comp-stat-icon">
                  <Users className="w-5 h-5 text-[#00ef43]" />
                </span>
                <small>Sellers na disputa</small>
                <b>{fmt.format(totalCompetitors)}</b>
                <span className="comp-stat-desc">
                  Comunidade MasterClub reunida no mesmo objetivo.
                </span>
              </div>

              {/* Status Pessoal do Usuário Conectado */}
              {userParticipation && (
                <div className="bg-[#17231b] border border-[#2e4c35] rounded-xl p-3.5 text-xs space-y-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[#00ef43] font-bold flex items-center gap-1 text-[11px] uppercase tracking-wider">
                      <UserCheck className="w-3.5 h-3.5" /> Você está na disputa
                    </span>
                    <button
                      onClick={() => {
                        setFormEmail(userParticipation.mastershopEmail);
                        setFormPublicName(userParticipation.publicName);
                        setModalStep('connection');
                      }}
                      className="text-gray-400 hover:text-white transition-colors"
                      title="Editar inscrição"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <div className="text-gray-200">
                    Nome público: <strong className="text-white">{userParticipation.publicName}</strong>
                  </div>
                  <div className="text-gray-300 text-[11px]">
                    Entregas apuradas: <b className="text-[#00ef43]">{userParticipation.deliveredOrders}</b>
                  </div>
                </div>
              )}
            </div>

            <div className="comp-demo-label mt-4">
              <i /> Sincronização em tempo real ativa
            </div>
          </aside>
        </div>

        {/* Linha de Ação Principal (CTA) */}
        <div className="comp-join-row">
          <button
            onClick={() => setModalStep('connection')}
            className="comp-join-btn group"
          >
            {userParticipation ? 'MINHA PARTICIPAÇÃO NO CHALLENGE' : 'PARTICIPAR DO CHALLENGE'}
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </button>
          <span>
            {userParticipation 
              ? `Inscrito como "${userParticipation.publicName}". Suas entregas apuradas continuam ativas.` 
              : 'Inscrições pausadas temporariamente para atualização da integração oficial com a MasterShop.'}
          </span>
        </div>

        {/* Seção de Premiações */}
        <section id="premios">
          <div className="comp-section-title">
            <div>
              <h2>Premiações</h2>
              <p>Três prêmios no pódio. Brindes exclusivos para os cinco primeiros.</p>
            </div>
            <span className="tiny">TEMPORADA 2026</span>
          </div>

          <div className="comp-prizes">
            {/* 1º Lugar */}
            <article className="comp-prize">
              <img className="comp-prize-photo" src="/images/black-challenge/san-andres.webp" alt="Vista aérea do mar de San Andrés" loading="lazy" />
              <span className="place">01 / PRIMEIRO LUGAR</span>
              <h3>Medellín + San Andrés</h3>
              <p>
                Viagem para um ganhador com os embaixadores e donos da plataforma. Teto proposto para o pacote: R$ 12.000.
              </p>
            </article>

            {/* 2º Lugar */}
            <article className="comp-prize">
              <img className="comp-prize-photo" src="/images/black-challenge/kit-da-escala.webp" alt="MacBook Air ao lado de uma lata de Monster branco" loading="lazy" />
              <span className="place">02 / SEGUNDO LUGAR</span>
              <h3>Kit da Escala</h3>
              <p>MacBook Air na mesa, Monster branco do lado. Quem escala sabe.</p>
            </article>

            {/* 3º Lugar */}
            <article className="comp-prize">
              <img className="comp-prize-photo" src="/images/black-challenge/iphone.webp" alt="Vista traseira de um iPhone 17 Pro Max" loading="lazy" />
              <span className="place">03 / TERCEIRO LUGAR</span>
              <h3>iPhone 17 Pro Max</h3>
              <p>O terceiro maior número de entregas leva o iPhone.</p>
            </article>
          </div>
        </section>

        {/* Faixa de Brinde Top 5 */}
        <div className="comp-gift-band">
          <b>TOP 5</b>
          <span>Cada um dos cinco primeiros leva o kit MasterClub: camisa, caderno e caneca, com envio incluído.</span>
        </div>

        {/* Seção de Regulamento e Regras */}
        <section id="regulamento" className="pt-6">
          <div className="comp-section-title">
            <div>
              <h2>Regras da competição</h2>
              <p>O que conta para o ranking e como participar.</p>
            </div>
          </div>

          <dl className="comp-rules-list">
            <div className="comp-rule-item">
              <dt>01 · Quem participa</dt>
              <dd>
                Sellers que operam na MasterShop. Leia as regras, vincule sua conta pelo hub e marque a MasterClub ao divulgar sua participação. Divulgação não soma pontos.
              </dd>
            </div>

            <div className="comp-rule-item">
              <dt>02 · Quando vender</dt>
              <dd>
                Pedidos criados de 1º de novembro a 15 de dezembro de 2026, inclusive, no horário de Bogotá.
              </dd>
            </div>

            <div className="comp-rule-item">
              <dt>03 · Como pontuar e Países Válidos</dt>
              <dd>
                Para entrar na classificação e concorrer aos prêmios, o seller precisa ter <strong className="text-white font-semibold">taxa de efetividade acima de 50% na MasterShop</strong>. Com 50% ou menos, o seller não se classifica. Um pedido elegível entregue vale um ponto para o seller responsável. 
                <span className="block mt-1.5 text-gray-200">
                  🌎 <strong className="text-[#00ef43]">Abrangência internacional:</strong> Serão consideradas as entregas de todos os países atendidos atualmente pela MasterShop: <strong className="text-white">🇨🇴 Colômbia</strong>, <strong className="text-white">🇵🇪 Peru</strong> e <strong className="text-white">🇪🇨 Equador</strong>.
                </span>
                <span className="block mt-1 text-gray-400">
                  Pedidos só gerados ou em trânsito não pontuam. Duplicados, testes, cancelamentos e devoluções não contam.
                </span>
              </dd>
            </div>

            <div className="comp-rule-item">
              <dt>04 · Como termina</dt>
              <dd>
                15/12 encerra a janela de vendas. O prazo adicional para confirmar entregas será divulgado antes da inscrição oficial. A classificação é provisória até a conferência dos pedidos; só depois serão anunciados os vencedores.
              </dd>
            </div>

            <div className="comp-rule-item">
              <dt>05 · O que você disputa</dt>
              <dd>
                1º: viagem para uma pessoa a Medellín e San Andrés com os embaixadores e donos da plataforma. Na proposta de regulamento, a organização reserva passagens, hospedagem, traslados, refeições e experiência dentro do limite total de R$ 12.000. Acompanhantes, compras pessoais e upgrades opcionais não estão incluídos. Datas e origem serão definidas antes da abertura oficial. 2º: Kit da Escala — um MacBook Air com chip M4 ou M5, conforme disponibilidade, e uma lata de Monster branco. 3º: iPhone 17 Pro Max. Do 1º ao 5º: um kit MasterClub com camisa, caderno e caneca, com envio incluído.
              </dd>
            </div>
          </dl>

          <p className="comp-draft-note">
            Regulamento oficial do Black Challenge 2026. A apuração é realizada diretamente pela API oficial de parceiros da MasterShop.
          </p>
        </section>

        {/* Rodapé Limpo (sem hyperlink de testes) */}
        <footer className="pt-8 pb-12 border-t border-[#242c34] flex flex-col sm:flex-row items-center justify-between text-xs text-[#89939e] gap-4">
          <span>© 2026 MasterClub</span>
          <span>Black Challenge · America/Bogota</span>
        </footer>

      </div>

      {/* Modal 1: Regras (Etapa 1 de 2) */}
      {modalStep === 'rules' && (
        <div 
          className="comp-modal-overlay" 
          onClick={() => setModalStep('none')}
        >
          <div 
            className="comp-modal" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-2">
              <span className="comp-stage-label">PARTICIPAR · ETAPA 1 DE 2</span>
              <button 
                onClick={() => setModalStep('none')}
                className="text-gray-400 hover:text-white p-1 rounded-md transition-colors"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <h2>Antes de entrar na disputa</h2>

            {/* Aviso: Atualização da Plataforma */}
            <div className="my-3.5 p-3.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-start gap-3 text-left">
              <RefreshCw className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-emerald-300 font-semibold text-xs tracking-wide">
                  Novas inscrições temporariamente em pausa para atualização da plataforma.
                </p>
                <p className="text-gray-400 text-[11px] mt-1 leading-relaxed">
                  Estamos aprimorando os conectores de sincronização de vendas com a MasterShop. As entregas e pedidos dos competidores já inscritos continuam sendo contabilizados normalmente.
                </p>
              </div>
            </div>

            <dl className="comp-rules-list my-4 max-h-[45vh] overflow-y-auto">
              <div className="comp-rule-item">
                <dt>01 · Quem participa</dt>
                <dd>Sellers que operam na MasterShop. Leia as regras, vincule sua conta pelo hub e marque a MasterClub ao divulgar sua participação.</dd>
              </div>
              <div className="comp-rule-item">
                <dt>02 · Quando vender</dt>
                <dd>Pedidos criados de 1º de novembro a 15 de dezembro de 2026, inclusive, no horário de Bogotá.</dd>
              </div>
              <div className="comp-rule-item">
                <dt>03 · Como pontuar e Países Válidos</dt>
                <dd>Requer <strong className="text-white">taxa de efetividade acima de 50% na MasterShop</strong>. Cada pedido elegível entregue vale 1 ponto. Consideramos entregas de todos os países atendidos: 🇨🇴 Colômbia, 🇵🇪 Peru e 🇪🇨 Equador.</dd>
              </div>
              <div className="comp-rule-item">
                <dt>04 · Como termina</dt>
                <dd>15/12 encerra a janela de vendas. A apuração final será conferida antes do anúncio dos vencedores.</dd>
              </div>
              <div className="comp-rule-item">
                <dt>05 · O que você disputa</dt>
                <dd>1º lugar: Viagem para Medellín e San Andrés (teto R$ 12.000). 2º: Kit da Escala (MacBook Air + Monster branco). 3º: iPhone 17 Pro Max. Top 5: Kit exclusivo MasterClub.</dd>
              </div>
            </dl>

            <p className="comp-draft-note">
              Ao participar, seus pedidos entregues no período oficial serão contabilizados automaticamente no ranking.
            </p>

            <label className="comp-rule-check">
              <input 
                type="checkbox" 
                checked={hasAgreedToRules}
                onChange={(e) => setHasAgreedToRules(e.target.checked)}
              />
              <span>Li e concordo com os termos e regras do Black Challenge.</span>
            </label>

            <button
              disabled={true}
              className="w-full py-3.5 px-4 rounded-lg font-bold text-sm flex items-center justify-center gap-2 bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700 transition-all duration-200"
            >
              Inscrições pausadas temporariamente
            </button>
          </div>
        </div>
      )}

      {/* Modal 2: Inscrições Temporariamente Pausadas para Atualização Oficial de Segurança */}
      {modalStep === 'connection' && (
        <div 
          className="comp-modal-overlay" 
          onClick={() => setModalStep('none')}
        >
          <div 
            className="comp-modal max-w-md w-full" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-2">
              <span className="comp-stage-label text-[#00ef43]">COMUNICADO OFICIAL · ATUALIZAÇÃO</span>
              <button 
                onClick={() => setModalStep('none')}
                className="text-gray-400 hover:text-white p-1 rounded-md transition-colors"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="comp-connection-icon !bg-[#00ef43]/10 !border-[#00ef43]/30 !text-[#00ef43]">
              <RefreshCw className="w-6 h-6" />
            </div>

            <h2>Atualização da Integração</h2>
            <p className="text-xs text-gray-300 leading-relaxed mb-4">
              Estamos realizando uma otimização programada nos servidores de apuração de pedidos junto à MasterShop para garantir maior velocidade e precisão no processamento das entregas.
            </p>

            <div className="space-y-3 mb-5">
              <div className="p-3 bg-[#17201a] border border-[#23422a] rounded-xl text-xs space-y-1">
                <div className="flex items-center gap-1.5 text-[#00ef43] font-bold">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <span>Apuração normal em andamento</span>
                </div>
                <p className="text-gray-400">
                  Todas as vendas e entregas realizadas até o momento continuam sendo computadas e sincronizadas normalmente no ranking.
                </p>
              </div>

              <div className="p-3 bg-[#14181d] border border-[#26313d] rounded-xl text-xs space-y-1">
                <div className="flex items-center gap-1.5 text-gray-200 font-semibold">
                  <Clock className="w-4 h-4 text-[#00ef43] shrink-0" />
                  <span>Novas inscrições reabertas em breve</span>
                </div>
                <p className="text-gray-400">
                  A vinculação de novos participantes será retomada nos próximos dias assim que concluirmos esta melhoria técnica.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setModalStep('none')}
              className="w-full py-3.5 px-4 rounded-lg font-bold text-sm bg-[#00ef43] text-[#071b0c] hover:bg-[#00df3c] cursor-pointer shadow-lg shadow-[#00ef43]/20 transition-all flex items-center justify-center gap-2"
            >
              Acompanhar o Ranking
            </button>
          </div>
        </div>
      )}

      {/* Modal 3: Moderação Admin - Editar Nome do Vendedor */}
      {isAdmin && editingPlayer && (
        <div 
          className="comp-modal-overlay" 
          onClick={() => !savingName && setEditingPlayer(null)}
        >
          <div 
            className="comp-modal max-w-md w-full" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-2">
              <span className="comp-stage-label text-[#00ef43]">MODERAÇÃO ADMIN · RANKING</span>
              <button 
                onClick={() => !savingName && setEditingPlayer(null)}
                className="text-gray-400 hover:text-white p-1 rounded-md transition-colors cursor-pointer"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="comp-connection-icon !bg-[#00ef43]/10 !border-[#00ef43]/30 !text-[#00ef43]">
              <Pencil className="w-6 h-6" />
            </div>

            <h2>Editar Nome do Vendedor</h2>
            <p className="text-xs text-gray-400 leading-relaxed mb-4">
              Altere o nome exibido no ranking oficial para o nome real do seller. Esta mudança recalcula o placar público imediatamente.
            </p>

            {editingPlayer.email && (
              <div className="mb-4 p-3 rounded-lg bg-[#142618] border border-[#2b5932] flex items-center justify-between text-xs">
                <span className="text-gray-300 font-medium">E-mail MasterShop:</span>
                <span className="text-[#00ef43] font-mono font-semibold flex items-center gap-1.5 select-all">
                  <Mail className="w-3.5 h-3.5" />
                  {editingPlayer.email}
                </span>
              </div>
            )}

            <form onSubmit={handleSaveName} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1.5">
                  Nome Real do Vendedor
                </label>
                <input
                  type="text"
                  required
                  autoFocus
                  placeholder="Ex: Carlos Alberto, Bruno Matheus"
                  value={editingNameInput}
                  onChange={(e) => setEditingNameInput(e.target.value)}
                  maxLength={40}
                  className="w-full bg-[#101418] border border-[#2b3540] rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#00ef43]"
                />
              </div>

              {editNameError && (
                <div className="p-3 bg-red-950/50 border border-red-800 text-red-300 text-xs rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                  <span>{editNameError}</span>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  disabled={savingName}
                  onClick={() => setEditingPlayer(null)}
                  className="flex-1 py-3 px-4 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-semibold transition-colors disabled:opacity-50 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingName || !editingNameInput.trim()}
                  className="flex-1 py-3 px-4 rounded-lg bg-[#00ef43] hover:bg-[#00df3c] text-[#071b0c] text-xs font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-1.5 shadow-lg shadow-[#00ef43]/20 cursor-pointer"
                >
                  {savingName ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Salvar Alteração
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default CompetitionPage;
